import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCrosshairs } from 'react-icons/fa';
import { getAllBookStores } from 'api/company';
import { useUserStore, useCompanyStore } from 'stores';
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
import Button from 'components/userzone/buttons/ButtonCustom';
import { CustomSelectComponent } from 'components/CustomSelectComponent';

const BenefitedBookStore = ({ setFormInfo, handleChangeSave, errorSave, successSave, loading }) => {
  const { benefitedBookstore } = useUserStore();
  const { t } = useTranslation(['register', 'backoffice']);
  const [benefitedBookstoreOptions, setBenefitedBookstoreOptions] = useState([]);
  const { assignedLibrary } = useAssignedLibrary();
  const { is_single_company: isMonoCliente } = useCompanyStore();
  const searchTimeoutRef = useRef(null);

  const handleBookstoreSelection = (selectedStore) => {
    if (selectedStore) {
      setFormInfo((prevFormInfo) => ({
        ...prevFormInfo,
        benefited_bookstore: selectedStore,
      }));
    }
  };

  function isEmptyObject(object) {
    return Object.values(object).every((value) => value === '');
  }

  const searchBookStores = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setBenefitedBookstoreOptions([]);
      return;
    }

    try {
      const { status, data } = await getAllBookStores({ 
        search: searchTerm,
        page: 1,
        page_size: 20
      });
      if (status === 200 && data?.results) {
        const bookStores = data.results.map((store) => ({
          value: store?.id,
          label: store?.name,
          subtitle: store?.direccion,
        }));
        setBenefitedBookstoreOptions(bookStores);
      } else {
        setBenefitedBookstoreOptions([]);
      }
    } catch (error) {
      console.error('Error fetching book stores:', error);
      setBenefitedBookstoreOptions([]);
    }
  }, []);

  const handleInputChange = useCallback((searchTerm) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm || searchTerm.length < 2) {
      setBenefitedBookstoreOptions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchBookStores(searchTerm);
    }, 500);
  }, [searchBookStores]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Row className="section d-flex section-responsive">
      <Col md={8}>
        <CustomSelectComponent
          handleSelection={handleBookstoreSelection}
          options={benefitedBookstoreOptions}
          isDisabled={isMonoCliente}
          onInputChange={handleInputChange}
          {...(!isEmptyObject(benefitedBookstore || {}) && { selectedOptionDefault: benefitedBookstore })}
        />
      </Col>
      <Col md={12}>
        {!isMonoCliente && (
          <p className="locate-me mt-2">
            <Link to={`/${assignedLibrary}/librerias/mapa`}>
              <FaCrosshairs />
              <span id="loc">{t('currentlocation')}</span>
            </Link>
          </p>
        )}
      </Col>
      {!isMonoCliente && (
        <Col md={12} id="bookshop-save" className="d-flex w-100 justify-content-end gap-2">
          {errorSave !== '' && (
            <div className="alert-danger align-items-center">
              <span className="m-0 text-danger">{errorSave}</span>
            </div>
          )}
          {successSave && (
            <div className="alert-success align-items-center">
              <span className="m-0 correct-message">{t('personalinformation')}</span>
            </div>
          )}
          <Button onClick={handleChangeSave} variant="primary" extraClass="m-2 width-btn-save" loading={loading}>
            {t('save', { ns: 'backoffice' }).toUpperCase()}
          </Button>
        </Col>
      )}
    </Row>
  );
};

export default BenefitedBookStore;
